import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../axiosConfig';
import Sidebar from '../../components/ui/Sidebar';
import { useToast } from '../../components/ui/Toast';
import { PackagePlus, AlertCircle, TrendingUp, AlertTriangle } from 'lucide-react';
import './ReceiveStockSidebar.css';

/**
 * ReceiveStockSidebar — Phase 4.A.6
 *
 * Replaces the silent quantity bump on the product editor. When a
 * merchant clicks "Receive new stock", this sidebar captures the
 * intake metadata (quantity, expiry, cost, supplier ref, batch ref,
 * notes) and POSTs to /merchants/products/:id/receive-stock which
 * creates a ProductBatch row via inventoryService.receiveStock.
 *
 * After a successful save the parent gets onSuccess({newQuantity})
 * so it can refresh the product detail without a full reload.
 */
export default function ReceiveStockSidebar({
  isOpen,
  onClose,
  product,
  defaultCostPerUnit,
  defaultExpiryDate,
  onSuccess,
}) {
  const toast = useToast();
  const [quantity, setQuantity] = useState('');
  const [expiryDate, setExpiryDate] = useState(
    defaultExpiryDate ? String(defaultExpiryDate).slice(0, 10) : ''
  );
  const [costPerUnit, setCostPerUnit] = useState(
    defaultCostPerUnit != null && defaultCostPerUnit !== '' ? String(defaultCostPerUnit) : ''
  );
  const [supplierReference, setSupplierReference] = useState('');
  const [batchReference, setBatchReference] = useState('');
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState('PURCHASE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Phase 4.A.6 polish — variant-aware receive. A variant product has
  // its stock attached to specific variants (Small/Red etc.), so we
  // need to know WHICH variant the new batch is for. Extracted from
  // the product's variations JSON; defaults to first if not chosen.
  const variants = useMemo(() => {
    const v = product?.variations;
    if (!v) return [];
    if (Array.isArray(v)) return v;
    if (Array.isArray(v.variants)) return v.variants;
    return [];
  }, [product]);
  const isVariantProduct = Boolean(product?.hasVariations) && variants.length > 0;
  const [variantId, setVariantId] = useState('');

  // Phase 4.A.6 polish — fetched markup context for the suggested
  // sell-price calc. { currentSellPrice, currentCostPrice,
  // markupPercent, markupSource: 'product' | 'category' | 'store' | null,
  // autoApply: boolean }
  const [markupCtx, setMarkupCtx] = useState(null);
  // When a markup is set, the merchant's declared intent is "keep this
  // margin." Defaulting to "apply" honors that intent — they opt OUT
  // (Keep current price) only for exceptions like clearance pricing or
  // contractual deals. Industry standard: Lightspeed, ERPNext, NetSuite.
  const [keepCurrentPrice, setKeepCurrentPrice] = useState(false);
  // When the new sell price would push above (or to) the current
  // Compare At Price, that compare-at becomes nonsensical ("Was ₦X →
  // Now ₦higher" strikethrough). Default to true so we fix it for the
  // merchant unless they uncheck.
  const [clearCompareAtPrice, setClearCompareAtPrice] = useState(true);

  // Reset variant selection whenever the sidebar opens. Default to
  // the first variant on variant products so the merchant sees a
  // sensible pre-selection rather than an empty picker.
  useEffect(() => {
    if (!isOpen) return;
    if (isVariantProduct) {
      setVariantId((current) => current || variants[0]?.id || '');
    } else {
      setVariantId('');
    }
  }, [isOpen, isVariantProduct, variants]);

  // Load the markup context once the sidebar opens with a product. For
  // variant products we also refetch when the merchant switches variants
  // — the "current sell/cost" baseline comes from the variant slot.
  useEffect(() => {
    if (!isOpen || !product?.id) {
      setMarkupCtx(null);
      setKeepCurrentPrice(false);
      return;
    }
    // Hold the fetch until a variant has been picked on a variant
    // product so we don't seed UI with parent-product pricing.
    if (isVariantProduct && !variantId) return;
    let cancelled = false;
    (async () => {
      try {
        const params = isVariantProduct && variantId ? { variantId } : undefined;
        const { data } = await axios.get(
          `/merchants/products/${product.id}/markup-context`,
          params ? { params } : undefined
        );
        if (!cancelled) {
          setMarkupCtx(data);
          // Phase 4.A.6 follow-on — respect the merchant's store-wide
          // auto-apply preference. autoApply=true (default) → checkbox
          // unchecked = price WILL update on save. autoApply=false →
          // checkbox checked = price stays put unless merchant opts in.
          setKeepCurrentPrice(data?.autoApply === false);
          setClearCompareAtPrice(true);
        }
      } catch (e) {
        // Non-fatal — sidebar still works without the suggestion block.
        console.warn('[ReceiveStock] markup-context fetch failed', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, product?.id, isVariantProduct, variantId]);

  // Recompute suggested sell price from local cost + fetched markup %.
  // useMemo so the comparison block updates as the merchant types.
  const suggestion = useMemo(() => {
    if (!markupCtx) return null;
    const cost = parseFloat(costPerUnit);
    if (!Number.isFinite(cost) || cost <= 0) return null;
    if (markupCtx.markupPercent == null) return null;
    const suggested = Number((cost * (1 + Number(markupCtx.markupPercent) / 100)).toFixed(2));
    const current = Number(markupCtx.currentSellPrice || 0);
    return {
      cost,
      markupPercent: Number(markupCtx.markupPercent),
      markupSource: markupCtx.markupSource,
      suggested,
      current,
      delta: suggested - current,
      // >= because exactly-at-cost is also a "watch out" moment: zero
      // margin per sale. The merchant might be intentionally clearing
      // dead stock, but they should at least be told.
      wouldSellAtLoss: cost >= current
    };
  }, [costPerUnit, markupCtx]);

  // The "effective" sell price once we save — equals the suggestion
  // when applying, equals current when opting out. Drives both the
  // compare-at warning and the cost-update notice below.
  const effectiveSellPrice = useMemo(() => {
    if (suggestion && !keepCurrentPrice) return suggestion.suggested;
    return markupCtx?.currentSellPrice ?? 0;
  }, [suggestion, keepCurrentPrice, markupCtx]);

  // Compare At Price becomes meaningless when it falls at or below the
  // effective sell price — the storefront would render "Was ₦X → Now
  // ₦higher" which is the opposite of what compare-at means.
  const compareAtIsStale = useMemo(() => {
    const cap = markupCtx?.currentCompareAtPrice;
    if (cap == null) return false;
    return Number(cap) <= Number(effectiveSellPrice);
  }, [markupCtx, effectiveSellPrice]);

  const reset = () => {
    setQuantity('');
    setExpiryDate(defaultExpiryDate ? String(defaultExpiryDate).slice(0, 10) : '');
    setCostPerUnit(defaultCostPerUnit != null && defaultCostPerUnit !== '' ? String(defaultCostPerUnit) : '');
    setSupplierReference('');
    setBatchReference('');
    setNotes('');
    setSource('PURCHASE');
    setError(null);
    setKeepCurrentPrice(false);
    setClearCompareAtPrice(true);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    const qty = parseInt(quantity, 10);
    if (!Number.isInteger(qty) || qty <= 0) {
      setError('Enter a quantity greater than zero.');
      return;
    }
    if (isVariantProduct && !variantId) {
      setError('Pick which variant this stock is for.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `/merchants/products/${product.id}/receive-stock`,
        {
          quantity: qty,
          variantId: isVariantProduct ? variantId : undefined,
          expiryDate: expiryDate || null,
          costPerUnit: costPerUnit ? parseFloat(costPerUnit) : undefined,
          supplierReference: supplierReference.trim() || undefined,
          batchReference: batchReference.trim() || undefined,
          notes: notes.trim() || undefined,
          source,
          // Apply the markup-suggested price by default; only skip if
          // the merchant explicitly ticked "Keep current price".
          applyNewSellPrice: !keepCurrentPrice,
          // Clear stale compare-at only when (a) the merchant kept the
          // checkbox checked and (b) it actually is stale post-save.
          clearCompareAtPrice: clearCompareAtPrice && compareAtIsStale,
        }
      );
      const priceMsg = data?.pricing?.priceUpdated
        ? ` Sell price updated to ₦${Number(data.pricing.appliedSellPrice).toLocaleString()}.`
        : '';
      const costMsg = data?.pricing?.costPriceUpdated
        ? ` Unit cost updated to ₦${Number(data.pricing.newCostPrice).toLocaleString()}.`
        : '';
      const capMsg = data?.pricing?.compareAtPriceCleared
        ? ' Compare-at price cleared.'
        : '';
      toast.success(
        `Received ${qty} ${qty === 1 ? 'unit' : 'units'} of ${product.name}.${priceMsg}${costMsg}${capMsg}`
      );
      onSuccess?.({
        newQuantity: data?.newQuantity,
        batchId: data?.batchId,
        priceUpdated: data?.pricing?.priceUpdated,
        appliedSellPrice: data?.pricing?.appliedSellPrice,
        costPriceUpdated: data?.pricing?.costPriceUpdated,
        newCostPrice: data?.pricing?.newCostPrice,
        compareAtPriceCleared: data?.pricing?.compareAtPriceCleared,
      });
      reset();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to receive stock';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={handleClose}
      title="Receive new stock"
      subtitle={product ? `Adding to "${product.name}"` : null}
      width="md"
    >
      <form className="rcv-stock-form" onSubmit={handleSubmit}>
        {error && (
          <div className="rcv-stock-error" role="alert">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Phase 4.A.6 polish — variant picker for variant products.
            Variant stock lives at the variant slot, so the merchant
            has to tell us WHICH variant the new batch belongs to. */}
        {isVariantProduct && (
          <div className="rcv-stock-row">
            <label className="rcv-stock-label">
              Variant <span className="rcv-stock-required">*</span>
            </label>
            <select
              className="rcv-stock-input"
              value={variantId}
              onChange={(e) => setVariantId(e.target.value)}
              required
            >
              {variants.map((v) => {
                const combo = v.combination
                  ? Object.entries(v.combination).map(([k, val]) => `${k}: ${val}`).join(' · ')
                  : v.name || v.sku || v.id;
                const stockNote = Number.isFinite(Number(v.quantity))
                  ? ` — ${v.quantity} in stock`
                  : '';
                return (
                  <option key={v.id} value={v.id}>
                    {combo}{stockNote}
                  </option>
                );
              })}
            </select>
            <span className="rcv-stock-hint">
              Stock for variant products is tracked per variant. Pick the one you're receiving.
            </span>
          </div>
        )}

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">
            Quantity received <span className="rcv-stock-required">*</span>
          </label>
          <input
            type="number"
            min="1"
            step="1"
            className="rcv-stock-input"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="e.g. 50"
            autoFocus
            required
          />
        </div>

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">Cost per unit (₦)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="rcv-stock-input"
            value={costPerUnit}
            onChange={(e) => setCostPerUnit(e.target.value)}
            placeholder={defaultCostPerUnit ? `default ${defaultCostPerUnit}` : 'e.g. 3200'}
          />
          <span className="rcv-stock-hint">
            What you paid the supplier for this batch. Saved exactly as entered so your margin reports stay accurate even if the product's cost price changes later.
          </span>
        </div>

        {/* Phase 4.A.6 polish — markup-based price update. When a markup
            is set, the merchant's intent is "keep this margin." Default
            is therefore APPLY the suggested new price; opt-out via
            "Keep current price instead." Industry standard: Lightspeed,
            ERPNext, NetSuite. */}
        {suggestion && Math.abs(suggestion.delta) >= 0.01 && (
          <div className={`rcv-stock-suggestion ${keepCurrentPrice ? 'is-opted-out' : ''}`}>
            <div className="rcv-stock-suggestion-header">
              <TrendingUp size={16} />
              <span>
                {keepCurrentPrice
                  ? `Keeping sell price at ₦${suggestion.current.toLocaleString()}`
                  : `Sell price will update to ₦${suggestion.suggested.toLocaleString()}`}
              </span>
            </div>
            <div className="rcv-stock-suggestion-calc">
              ₦{suggestion.cost.toLocaleString()} cost × {suggestion.markupPercent}% markup
              {' '}({
                suggestion.markupSource === 'product'
                  ? 'product override'
                  : suggestion.markupSource === 'category'
                    ? 'category override'
                    : 'store default'
              }){' '}
              = <strong>₦{suggestion.suggested.toLocaleString()}</strong>
            </div>
            <div className="rcv-stock-suggestion-compare">
              Current sell price: <strong>₦{suggestion.current.toLocaleString()}</strong>
              {' '}
              <span className={`rcv-stock-delta ${suggestion.delta > 0 ? 'up' : 'down'}`}>
                ({suggestion.delta > 0 ? '+' : ''}₦{suggestion.delta.toLocaleString()})
              </span>
            </div>
            <label className="rcv-stock-apply">
              <input
                type="checkbox"
                checked={keepCurrentPrice}
                onChange={(e) => setKeepCurrentPrice(e.target.checked)}
              />
              <span>
                Keep current price (₦{suggestion.current.toLocaleString()}) instead
              </span>
            </label>
          </div>
        )}

        {/* Loss-warning banner — fires only when the merchant has opted
            OUT of the auto-update AND the resulting margin would be
            zero or negative. If they let the markup drive the price,
            margin is preserved by construction and no warning needed. */}
        {suggestion?.wouldSellAtLoss && keepCurrentPrice && (
          <div className="rcv-stock-warning" role="alert">
            <AlertTriangle size={16} />
            <span>
              Your cost (₦{suggestion.cost.toLocaleString()}) is at or above your sell price (₦{suggestion.current.toLocaleString()}). With current price kept, every sale from this batch will break even at best, or lose money. Untick "Keep current price" above to let the markup auto-adjust.
            </span>
          </div>
        )}

        {/* No-markup loss warning — cost ≥ sell AND no markup % is set
            anywhere. The merchant has no automated way out; the system
            can only tell them, not fix it for them. */}
        {markupCtx && markupCtx.markupPercent == null &&
         parseFloat(costPerUnit) > 0 &&
         parseFloat(costPerUnit) >= Number(markupCtx.currentSellPrice || 0) && (
          <div className="rcv-stock-warning" role="alert">
            <AlertTriangle size={16} />
            <span>
              Your cost (₦{parseFloat(costPerUnit).toLocaleString()}) is at or above your sell price (₦{Number(markupCtx.currentSellPrice || 0).toLocaleString()}). Sales from this batch will break even at best, or lose money. Set a Markup % on the product (Pricing tab) so the system can suggest a new sell price automatically, or raise the sell price manually.
            </span>
          </div>
        )}

        {/* Phase 4.A.6 polish — stale compare-at warning. Fires when
            the effective sell price (suggested if applying, current if
            keeping) is ≥ the existing compare-at, which would make the
            storefront render "Was ₦X → Now ₦higher" — a nonsensical
            discount strikethrough. Default to clearing on save; the
            merchant can opt out. */}
        {compareAtIsStale && (
          <div className="rcv-stock-compareat">
            <div className="rcv-stock-compareat-header">
              <AlertCircle size={16} />
              <span>Compare-At Price is now lower than the sell price</span>
            </div>
            <div className="rcv-stock-compareat-body">
              Your Compare-At Price (₦{Number(markupCtx.currentCompareAtPrice).toLocaleString()}) is at or below the new sell price (₦{Number(effectiveSellPrice).toLocaleString()}). On the storefront that would render as a fake discount — "was lower, now higher" — which confuses customers.
            </div>
            <label className="rcv-stock-apply">
              <input
                type="checkbox"
                checked={clearCompareAtPrice}
                onChange={(e) => setClearCompareAtPrice(e.target.checked)}
              />
              <span>
                Clear the Compare-At Price on save (recommended)
              </span>
            </label>
          </div>
        )}

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">Expiry date</label>
          <input
            type="date"
            className="rcv-stock-input"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
          <span className="rcv-stock-hint">
            Optional. Leave blank for non-perishables. Drives FEFO consumption and the Expiring Stock tab.
          </span>
          {(() => {
            if (!expiryDate) return null;
            // Compare against today's local calendar date — the
            // backend stores expiry as @db.Date so we match on day
            // boundaries, not timestamps.
            const today = new Date();
            const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            if (expiryDate >= todayKey) return null;
            return (
              <div className="rcv-stock-past-expiry" role="note">
                <strong>Heads up:</strong> this date is already past. The batch
                will be recorded in the <em>expired</em> bucket and won't count
                as sellable stock. Use this only for retroactively logging
                stock that arrived already expired.
              </div>
            );
          })()}
        </div>

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">Source</label>
          <select
            className="rcv-stock-input"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option value="PURCHASE">Purchase / supplier delivery</option>
            <option value="RETURN">Customer return</option>
            <option value="MANUAL_ADJUSTMENT">Manual adjustment / recount</option>
            <option value="TRANSFER">Transfer in from another location</option>
          </select>
        </div>

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">Supplier reference</label>
          <input
            type="text"
            className="rcv-stock-input"
            value={supplierReference}
            onChange={(e) => setSupplierReference(e.target.value)}
            placeholder="e.g. PO-2026-051 or invoice #"
            maxLength={120}
          />
        </div>

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">Batch / lot reference</label>
          <input
            type="text"
            className="rcv-stock-input"
            value={batchReference}
            onChange={(e) => setBatchReference(e.target.value)}
            placeholder="e.g. LOT-A23-09 (printed on packaging)"
            maxLength={120}
          />
        </div>

        <div className="rcv-stock-row">
          <label className="rcv-stock-label">Notes</label>
          <textarea
            className="rcv-stock-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional — anything you'd want to recall later about this delivery"
            rows={3}
            maxLength={1000}
          />
        </div>

        <div className="rcv-stock-actions">
          <button
            type="button"
            className="rcv-stock-btn rcv-stock-btn--secondary"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rcv-stock-btn rcv-stock-btn--primary"
            disabled={saving || !quantity}
          >
            <PackagePlus size={16} />
            {saving ? 'Saving…' : 'Receive stock'}
          </button>
        </div>
      </form>
    </Sidebar>
  );
}
